import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Wrench, Maximize2, Minimize2 } from 'lucide-react';
import { painelOficina } from '@/lib/painelOficina';
import { OficinaKanbanOrderCard } from '@/components/oficina/OficinaKanbanOrderCard';
import { fetchOficinaBoard, fetchOficinaCompanies } from '@/services/oficinaApi';

const POLL_MS = 60_000;

export const DashboardOficinaV2Page: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(searchParams.get('companyId') ?? '');
  const [isFs, setIsFs] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-oficina-dashboard'],
    queryFn: fetchOficinaCompanies,
  });

  useEffect(() => {
    if (companies.length && !companyId) {
      setCompanyId(companies[0]!.id);
    }
  }, [companies, companyId]);

  useEffect(() => {
    navigate(painelOficina(companyId || null).kanbanV2Location, { replace: true });
  }, [companyId, navigate]);

  const {
    data: boardPayload,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['oficina-board', companyId],
    queryFn: () => fetchOficinaBoard(companyId),
    enabled: Boolean(companyId),
    staleTime: 0,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const toggleFs = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().then(() => setIsFs(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFs(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const clockLabel = useMemo(() => format(now, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR }), [now]);

  const columns = boardPayload?.columns ?? [];

  const totalOrdensNoPainel = useMemo(
    () => columns.reduce((acc, col) => acc + col.ordens.length, 0),
    [columns]
  );

  const showTotalizadorPainel = Boolean(companyId) && !isLoading && !isError;

  return (
    <>
      <Helmet>
        <title>Painel Oficina v2 | PortalFly Zeh Motoca</title>
      </Helmet>
      <div
        className="flex h-full min-h-0 max-h-full flex-1 flex-col overflow-hidden bg-background p-3 md:p-4 lg:p-5"
        data-testid="page-dashboard-oficina-v2"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="grid shrink-0 grid-cols-1 items-center gap-3 lg:grid-cols-[1fr_auto_1fr] lg:gap-x-3 lg:gap-y-0">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end sm:gap-3 lg:gap-4 justify-self-start">
              <h1 className="font-heading text-xl font-semibold text-foreground md:text-2xl lg:text-3xl tracking-tight">
                Clientes em Atendimento
              </h1>
              {showTotalizadorPainel && (
                <div
                  className="flex shrink-0 flex-col items-start rounded-lg border border-border/70 bg-card/80 px-3 py-2 shadow-soft-sm backdrop-blur-sm sm:items-end sm:text-right sm:min-w-[140px]"
                  title="Total de ordens de serviço exibidas em todas as colunas do Kanban"
                >
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Total no painel
                  </span>
                  <span className="font-mono text-2xl font-bold tabular-nums leading-none text-foreground md:text-3xl">
                    {totalOrdensNoPainel}
                  </span>
                  <span className="text-[10px] text-muted-foreground">OS em todas as colunas</span>
                </div>
              )}
            </div>
            <div className="justify-self-center text-xs leading-none md:text-sm" title="Data e hora atuais">
              <div className="rounded-md border border-border/60 bg-card/70 px-3 py-2 font-mono text-[clamp(1rem,4vw,2.25rem)] tabular-nums text-foreground lg:px-4 lg:py-3">
                {clockLabel}
              </div>
            </div>
            <div className="flex w-full flex-col items-end gap-2 lg:w-auto lg:shrink-0 lg:justify-self-end">
              <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-end">
                <div className="min-w-[180px] max-w-full rounded-input border border-border/60 bg-card/60 p-1">
                  <Select
                    value={companyId}
                    onValueChange={setCompanyId}
                    placeholder="Selecione…"
                    className="border-0 bg-transparent text-sm focus-visible:ring-0 md:text-base"
                  >
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleFs}
                  className="border-border/70 bg-card/40 text-sm md:text-base"
                >
                  {isFs ? <Minimize2 className="mr-2 h-4 w-4 md:h-5 md:w-5" /> : <Maximize2 className="mr-2 h-4 w-4 md:h-5 md:w-5" />}
                  {isFs ? 'Sair' : 'Tela cheia'}
                </Button>
              </div>
            </div>
          </div>

          {!companyId && (
            <p className="shrink-0 text-base text-muted-foreground">Selecione uma empresa para carregar o painel.</p>
          )}

          {isError && (
            <p className="shrink-0 text-base text-destructive">
              {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Erro ao carregar o painel.'}
            </p>
          )}

          {isLoading && companyId && (
            <div className="flex min-h-0 flex-1 shrink-0 items-center justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}

          {companyId && !isLoading && !isError && (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <div className="flex min-h-0 min-w-0 flex-1 gap-2 overflow-x-hidden overflow-y-hidden [overflow-anchor:none]">
                {columns.length === 0 ? (
                  <Card className="min-h-0 min-w-0 flex-1 border-dashed bg-card/60">
                    <CardContent className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 py-8 text-center">
                      <Wrench className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground md:text-base">
                        Nenhuma OS em execução nesta empresa.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  columns.map((col) => (
                    <div
                      key={col.setor}
                      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border/70 bg-card/80 shadow-soft-sm backdrop-blur-sm"
                    >
                      <div className="sticky top-0 z-10 shrink-0 border-b border-border/70 bg-muted/50 px-2 py-2 backdrop-blur-sm md:px-3">
                        <div className="flex items-start justify-between gap-2">
                          <h2 className="min-w-0 flex-1 truncate text-xs font-semibold text-foreground md:text-sm lg:text-base">
                            {col.setor}
                          </h2>
                          <div
                            className="flex shrink-0 flex-col items-end gap-0.5 text-right"
                            title={`${col.ordens.length} ordem(ns) de serviço neste setor`}
                          >
                            <Badge
                              variant="secondary"
                              className="px-2 py-0.5 font-mono text-xs font-bold tabular-nums md:text-sm"
                            >
                              {col.ordens.length}
                            </Badge>
                            <span className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                              OS neste status
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="min-h-0 flex-1 basis-0 space-y-2 overflow-y-auto overflow-x-hidden overscroll-y-contain p-2 [-webkit-overflow-scrolling:touch] [overflow-anchor:none]">
                        {col.ordens.map((o) => (
                          <OficinaKanbanOrderCard key={o.id} order={o} now={now} compact />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
